import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import loc, { format } from 'owa-localize';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import {
    GetPermissionInfoStatus,
    RecipientPermissionInfo,
} from '../../schema/RecipientPermissionInfo';
import { SharingData } from '../../schema/SharingData';
import { SharingRecipientsInfo } from '../../schema/SharingRecipientsInfo';
import { shouldUseGenericError } from '../../selectors/sharingIssueDetectors/isPermissionNotSet';
import { SharingTipId } from '../../types/sharingTipId';
import { SharingTipRecipientInfo } from '../../types/SharingTipRecipientInfo';
import {
    MultipleLinksAreExpiredText,
    MultipleLinksWillExpireText,
    NoPermissionBigDLRecipientsText,
    NoPermissionToShareExternallyText,
    NoPermissionToShareText,
    NoSharingInfoText,
    SingleLinkIsExpiredText,
    SingleLinkWillExpireText,
    SingleRecipientCannotAccessFileText,
    WarningSharingCSLWithExternalRecipientsText,
    WarningSharingWithoutSettingPermissionsText,
    LessThanFourRecipientsCannotBeGrantedAccessFileText,
    TwoOrThreeRecipientsCannotAccessFileText,
    FourOrMoreRecipientsCannotBeGrantedAccessFileText,
    FourOrMoreRecipientsCannotAccessFileText,
} from './sharingTipIdToInfoBarString.locstring.json';
export interface InfoBarStringTemplates {
    singleFile: string;
    multipleFiles: string;
}

export function sharingTipIdToInfoBarString(
    sharingTipId: SharingTipId,
    recipientInfo: SharingTipRecipientInfo[],
    sharingData: SharingData[]
): InfoBarStringTemplates {
    switch (sharingTipId) {
        case SharingTipId.noPermissionToShare:
            return {
                singleFile: loc(NoPermissionToShareText),
                multipleFiles: loc(NoPermissionToShareText),
            };
        case SharingTipId.getSharingInfoFailed:
            return {
                singleFile: loc(NoSharingInfoText),
                multipleFiles: loc(NoSharingInfoText),
            };
        case SharingTipId.linkHasExpired:
            return {
                singleFile: loc(SingleLinkIsExpiredText),
                multipleFiles: loc(MultipleLinksAreExpiredText),
            };
        case SharingTipId.linkWillExpire:
            return {
                singleFile: loc(SingleLinkWillExpireText),
                multipleFiles: loc(MultipleLinksWillExpireText),
            };
        case SharingTipId.noPermissionToShareExternally:
            return {
                singleFile: loc(NoPermissionToShareExternallyText),
                multipleFiles: loc(NoPermissionToShareExternallyText),
            };
        case SharingTipId.externalRecipientCSL:
            return {
                singleFile: loc(WarningSharingCSLWithExternalRecipientsText),
                multipleFiles: loc(WarningSharingCSLWithExternalRecipientsText),
            };
        case SharingTipId.noPermissionSet:
            return {
                singleFile: getSingleFileStringFormatForIsPermissionNotSet(
                    sharingData[0].sharingRecipientsInfo,
                    recipientInfo,
                    sharingData[0].currentPermissionLevel
                ),
                multipleFiles: loc(WarningSharingWithoutSettingPermissionsText),
            };
        case SharingTipId.sharingWithLargeDL:
            const bigDLNames: string = getBigDLNames(recipientInfo);
            return {
                singleFile: format(loc(NoPermissionBigDLRecipientsText), bigDLNames, '{0}', '{1}'),
                multipleFiles: format(
                    loc(NoPermissionBigDLRecipientsText),
                    bigDLNames,
                    '{0}',
                    '{1}'
                ),
            };
        default:
            return assertNever(sharingTipId);
    }
}

const getBigDLNames = (recipientInfo: SharingTipRecipientInfo[] | null) => {
    if (recipientInfo) {
        const bigDLs = recipientInfo.filter(recipient => {
            return recipient.totalMemberCount > 100;
        });
        return bigDLs.map(DL => DL.recipient.persona.EmailAddress.Name).join(', ');
    }
    return '';
};

function getSingleFileStringFormatForIsPermissionNotSet(
    sharingRecipientsInfo: SharingRecipientsInfo,
    recipientInfo: SharingTipRecipientInfo[],
    permissionLevel: AttachmentPermissionLevel
): string {
    if (shouldUseGenericError(sharingRecipientsInfo, recipientInfo)) {
        return loc(WarningSharingWithoutSettingPermissionsText);
    }

    const allRecipients: RecipientPermissionInfo[] = sharingRecipientsInfo.recipientPermissionInfos;
    let canNotAccessRecipients: RecipientPermissionInfo[] = null;
    let canNotGrantAccess = false;
    if (permissionLevel === AttachmentPermissionLevel.None) {
        canNotAccessRecipients = allRecipients.filter(
            info =>
                !info.hasAccess &&
                info.getPermissionInfoStatus === GetPermissionInfoStatus.Succeeded
        );
        logUsage('RecipientCanNotAccess', { numOfRecipients: canNotAccessRecipients.length });
    } else if (
        permissionLevel === AttachmentPermissionLevel.View ||
        permissionLevel === AttachmentPermissionLevel.Edit
    ) {
        canNotGrantAccess = true;
        canNotAccessRecipients = allRecipients.filter(
            info =>
                !info.hasAccess &&
                !info.canHaveAccess &&
                info.getPermissionInfoStatus === GetPermissionInfoStatus.Succeeded
        );
        logUsage('RecipientCanNotHaveAccess', { numOfRecipients: canNotAccessRecipients.length });
    }

    if (canNotAccessRecipients !== null) {
        const totalCanNotAccessRecipientCount = canNotAccessRecipients.length;
        const recipientsNames = getAllRecipientsNames(canNotAccessRecipients.slice(0, 3));
        if (totalCanNotAccessRecipientCount === 1) {
            return format(
                loc(
                    canNotGrantAccess
                        ? LessThanFourRecipientsCannotBeGrantedAccessFileText
                        : SingleRecipientCannotAccessFileText
                ),
                recipientsNames,
                '{0}'
            );
        } else if (totalCanNotAccessRecipientCount < 4) {
            return format(
                loc(
                    canNotGrantAccess
                        ? LessThanFourRecipientsCannotBeGrantedAccessFileText
                        : TwoOrThreeRecipientsCannotAccessFileText
                ),
                recipientsNames,
                '{0}'
            );
        } else {
            return format(
                loc(
                    canNotGrantAccess
                        ? FourOrMoreRecipientsCannotBeGrantedAccessFileText
                        : FourOrMoreRecipientsCannotAccessFileText
                ),
                recipientsNames,
                '{0}'
            );
        }
    } else {
        return loc(WarningSharingWithoutSettingPermissionsText);
    }
}

function getAllRecipientsNames(canNotAccessRecipients: RecipientPermissionInfo[]): string {
    return canNotAccessRecipients.map(x => (x.name ? x.name : x.emailAddress)).join(', ');
}
